import { LabelService } from './../../../../../services/modules/stocks/labels';
import { Component, Inject, OnDestroy, ViewEncapsulation, ViewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { environment } from 'environments/environment';
import { Subscription } from 'rxjs';
import { ReportsService } from '~/services/modules/reports/reports';
import { ThemeManagerService } from '~/services/theme-manager';
import { LayoutManagerService, PORTAL_DATA } from '~/shared/components/layout/layout-manager';
import { LayoutComponent } from '~/shared/components/layout/layout.component';
import { LazyLoadingLibraryService } from '../LazyLoadingLibraryService';
import { WindowState } from '@progress/kendo-angular-dialog';

declare var Stimulsoft: any;

@Component({
    selector: 'app-report-designer',
    templateUrl: './reportdesigner.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportDesignerComponent implements OnDestroy {
  _active: boolean = true;
  reportId: number;
  IsBarcodeLabelLayout: boolean = false;
  theme: string;
  reportFileLoaded = false;
  viewerFileLoaded = false;
  designerFileLoaded = false;
  viewer: any;
  designer: any;
  stimulsoftReport: any;
  dbReport: any;
  public dataSet: any;
  options: any;
  windowState: WindowState = 'default';
  maximized: boolean;
  @ViewChild('mainWindow') kendoMainWindow: ElementRef;
  constructor(@Inject(PORTAL_DATA) public data: any,
    private _themeManager: ThemeManagerService,
    private _layoutManager: LayoutManagerService,
    private _lazyLoadingLibraryService: LazyLoadingLibraryService,
    private _reportService: ReportsService,
    private _layoutComponent: LayoutComponent,
    private _labelService: LabelService) {
    this._themeManager.themeChange.subscribe((data) => {
      this.theme = data;
    });

    this._layoutManager.applyOverly();
    // load stimulsoft js
    this._lazyLoadingLibraryService.loadJs('assets/scripts/stimulsoft.reports.pack.js').subscribe((x) => {
      this.reportFileLoaded = true;

      if (this.viewerFileLoaded && this.designerFileLoaded) {
        this.initPage();
      }
    });

    this._lazyLoadingLibraryService.loadJs('assets/scripts/stimulsoft.viewer.pack.js').subscribe((x) => {
      this.viewerFileLoaded = true;

      if (this.reportFileLoaded && this.designerFileLoaded) {
        this.initPage();
      }
    });

    this._lazyLoadingLibraryService.loadJs('assets/scripts/stimulsoft.designer.pack.js').subscribe((x) => {
      this.designerFileLoaded = true;

      if (this.reportFileLoaded && this.viewerFileLoaded) {
        this.initPage();
      }
    });
  }

  initPage() {
    this.initStimulsoft();
    this.options.appearance.fullScreenMode = false;
    this.options.appearance.showSaveDialog = false;
    this.designer = new Stimulsoft.Designer.StiDesigner(this.options, 'StiDesigner', false);

    this.designer.report = new Stimulsoft.Report.StiReport();

    this.dataSet = new Stimulsoft.System.Data.DataSet('DataSet');

    this.reportId = this.data.model.reportId;
    this.IsBarcodeLabelLayout = this.data.model.IsBarcodeLabelLayout;

    if (!this.IsBarcodeLabelLayout) {
      this._reportService.ReportById(this.reportId).subscribe((x) => {
        this.dbReport = x;

        if (this.dbReport.definition) {
          this.loadReportDefinition(this.dbReport.definition);
        }

        if (x.dataSource != undefined && x.dataSource.includes('GetReportbyDataSourceName')) {
          if (x.dataSource.includes('Report_')) {
            this._reportService.GetReportData(this.reportId, null, null).subscribe(
              (response) => {
                this.openReport(response as Array<any>);
              });
          }
          else {
            this._reportService.GetReportbyDataSourceName(this.dbReport.dataSource).subscribe((response) => {
              this.openReport(response as Array<any>);
            });
          }
        }
        else {
          if (this.dbReport.isForecast) {
            this._reportService.ProcessForecast(JSON.parse(this.dbReport.forecastConfig)).subscribe(
              (response) => {
                this.openReport(response as Array<any>);
              });
          }
          else {
            this._reportService.GetReportData_GET(this.dbReport.dataSource).subscribe(
              (response) => {
                this.openReport(response as Array<any>);
              });
          }
        }
      });
    }
    else {
      this._labelService.getLabelPresetById(this.reportId).subscribe((x: any) => {
        this.dbReport = x;

        if (this.dbReport?.reportDefinition) {
          this.loadReportDefinition(this.dbReport.reportDefinition);
        }
        if (x?.sourceView != undefined && x.sourceView.includes('GetReportbyDataSourceName') && !x.sourceView.includes('Report_')) {
          this._reportService.GetReportbyDataSourceName(this.dbReport.dataSource || this.dbReport.sourceView).subscribe(
            (response) => {
              this.openReport(response as Array<any>);
            },
            () => {
              this.openReportBarcodeLabel([]);
            }
          );
        } else {
          this._reportService.GetReportDataForBarcodeLabelDesigner(this.reportId, null).subscribe(
            (response) => {
              this.openReportBarcodeLabel(response as Array<any>);
            },
            () => {
              this.openReportBarcodeLabel([]);
            }
          );
        }
      }, () => {
        this.openReportBarcodeLabel([]);
      });
    }
  }

  initStimulsoft() {
    Stimulsoft.Base.StiLicense.key =
      '6vJhGtLLLz2GNviWmUTrhSqnOItdDwjBylQzQcAOiHkUqG1JqVi9xF1CsQtSylmNCw08f5US/nThDcH2mIyIhcb9q2' +
      'eL238CYd2WruGTJRQWTjv4Ojri9sZfpmunjsBAwTcJRB+7bLz0yAVCU6zESyCSrqmTsZGqSSjXhq82ybmFzAFUskAo' +
      '4HStx+kugphgl4gcDP0VI2kgHB6uQSOD2Xh8X+fmtkH1Q6RMplr+plgH0lQ35cSNk7kDJvc07nTPGVv+ixfOJYHyWO' +
      'o+A6M93S04A7HjpQFnJHCQqzJ7lJ3ZO9heRh2Vt5VBfUJoQZIFTpTNCAvXKZ6FmbFtIa8lKG0CGWFBJjDFkh6e6yVf' +
      'IRPFT48DDBGyC1ZZXHFTiCSxTxx9Rn+ZQrLDgvhZa4YFVIBm0Rrekr67eVHUqXpL7IiQA6kbCkJ/LZ6LCJwEL0TbBn' +
      'RWDfcx/r65mXVC/HoS+KZ3MWfB1fqZmiLfeWjTXXvnE3LjxgJYGobOqvdZns8d9c/Xq+cZtW6/wjnZL6SrHSjaUJPc' +
      'rLgViTggF/EzQGigT6qbzY1Cf5YNhWCuYaN+ou0vG9LVmMSR6oPxc5eHphDFlzEVgUHZWeyGlX0AVp7HVFVWUiJevw' +
      '9NbBkdYnR+0Iqs9pwBbvH3fgbFM8C9eyWM41wmJl16/jqohlJZHuw=';

    this.stimulsoftReport = new Stimulsoft.Report.StiReport();
    this.options = new Stimulsoft.Designer.StiDesignerOptions();

  }

  _onCancel() {
    this._closeForm();
  }

  _closeForm() {
    this._active = false;
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    this._layoutManager.removeOverlay();

    if (this.saveReportSub) this.saveReportSub.unsubscribe();
    if (this.saveBarcodeLabelSub) this.saveBarcodeLabelSub.unsubscribe();
    this.designer = undefined;
    this._layoutComponent.detachDialogForm();
  }
  _onStateChanges(e: any) {
    var element: any = this.kendoMainWindow;

    if (e === 'minimized') {
      this._layoutManager.removeOverlay();
      element.el.nativeElement.classList.remove('fullWidthFormForm');
    }
    else if (e === 'maximized') {
      this._layoutManager.applyOverly();
      element.el.nativeElement.classList.remove('fullWidthFormForm');
    }
    else if (e === 'default') {
      this._layoutManager.applyOverly();
      element.el.nativeElement.classList.add('fullWidthFormForm');
    }
  }

  maximizeWindow() {
    this.windowState = 'maximized';
    this.maximized = true;
  }

  public openReport(res: Array<any>) {
    if (res != undefined) {
      res = this.removeEmptyColumnsToSetNullInStimulsoft(res);
      this.dataSet.readJson(res.slice(0, 100));
    }

    this.stimulsoftReport.regData(this.dataSet.dataSetName, '', this.dataSet);
    this.stimulsoftReport.dictionary.synchronize();
    this.designer.report = this.stimulsoftReport;
    let that = this;
    this.designer.onSaveReport = function (args: any) {
      if (that.designer) {
        that.saveReport(args.fileName, args.report.saveEncryptedReportToString(environment.reportsKey));
      }
    };

    this.designer.renderHtml('designer');
  }

  saveReportSub!: Subscription;

  public saveReport(reportName: string, reportDefinition: string) {
    this.dbReport.definition = reportDefinition;

    if (this.saveReportSub) {
      this.saveReportSub.unsubscribe();
    }

    this.saveReportSub = this._reportService.saveReport(this.dbReport).subscribe(() => {
      this._layoutComponent.openPopup({
        title: 'Validation',
        message: 'Report saved',
        withoutConfirm: true
      });
    });
  }

  removeEmptyColumnsToSetNullInStimulsoft(data: any[]): any[] {
    data.forEach(item => {
      Object.keys(item).forEach(key => {
        if (!item[key]) {
          //delete item[key];
          item[key] = undefined;
        }
      });
    });

    return data;
  }

  public openReportBarcodeLabel(res: Array<any>) {
    if (res != undefined) {
      res = this.removeEmptyColumnsToSetNullInStimulsoft(res);
      this.dataSet.readJson(res.slice(0, 100));
    }

    this.stimulsoftReport.regData(this.dataSet.dataSetName, '', this.dataSet);
    this.stimulsoftReport.dictionary.synchronize();
    this.designer.report = this.stimulsoftReport;
    let that = this;
    this.designer.onSaveReport = function (args: any) {
      if (that.designer) {
        that.saveReportBarcodeLabel(args.fileName, args.report.saveEncryptedReportToString(environment.reportsKey));
      }
    };

    this.designer.renderHtml('designer');
  }

  saveBarcodeLabelSub!: Subscription;

  public saveReportBarcodeLabel(reportName: string, reportDefinition: string) {
    this.dbReport.reportDefinition = reportDefinition;
    if (this.saveBarcodeLabelSub) this.saveBarcodeLabelSub.unsubscribe();
    this.saveBarcodeLabelSub = this._labelService.updateLabelPreset(this.dbReport.id, this.dbReport).subscribe(() => {
      this._layoutComponent.openPopup({
        title: 'Validation',
        message: 'Barcode label saved',
        withoutConfirm: true
      });
      this._labelService.labelPrintUpdated.emit(this.dbReport);
    });
  }

  private loadReportDefinition(definition: string): void {
    if (!definition || !this.stimulsoftReport) return;
    const trimmed = typeof definition === 'string' ? definition.trim() : '';
    if (trimmed.startsWith('{') || trimmed.startsWith('<')) {
      try {
        this.stimulsoftReport.load(trimmed);
        return;
      } catch (e) {
        console.warn('Failed to load JSON report definition:', e);
      }
    }
    try {
      this.stimulsoftReport.loadEncryptedReport(definition, environment.reportsKey);
    } catch (e) {
      try {
        this.stimulsoftReport.load(definition);
      } catch (inner) {
        console.warn('Failed to load report definition:', inner);
      }
    }
  }
}

